import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { take } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { ProjectIntegration, ProviderType } from '../../models/project-integration.model';
import { RequirementTypeOption } from '../../models/requirement.model';
import { RequirementTypeService } from '../../services/requirement-type.service';
import { providerColor, providerLabel } from '../../shared/provider-meta';
import { mapTrackerError, TRACKER_ERROR_KEYS, TrackerError } from '../../shared/tracker-error';

interface HostRow {
  host: string;
  provider: ProviderType;
  integrationId: string;
  boardUrl: string;
  color: string;
  providerLabel: string;
  syncedTypes: string[];
  isSynced: boolean;
}

@Component({
  selector: 'app-typ-sync-card',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe],
  templateUrl: './typ-sync-card.component.html',
  styleUrl: './typ-sync-card.component.scss',
})
export class TypSyncCardComponent {
  readonly projectId = input.required<string>();
  readonly integrations = input.required<ProjectIntegration[]>();
  readonly requirementTypes = input.required<RequirementTypeOption[]>();

  readonly typesChanged = output<void>();

  private readonly service = inject(RequirementTypeService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);

  /**
   * NEX-1077 Review: pro Host separater Busy-Zustand. Vorher globaler Single-Slot,
   * womit bei Jira + Redmine der zweite Button klickbar aussah, aber nichts tat.
   */
  private readonly busyHosts = signal<ReadonlySet<string>>(new Set());

  readonly hosts = computed<HostRow[]>(() => {
    // Vorsatz: der erste Treffer pro Host gewinnt. Typen sind hostweit (BE stempelt
    // `source_host` per URI.getHost()), also fahren mehrere Boards am selben Host
    // die gleiche Typliste — deshalb ist die Integration hier austauschbar.
    const byHost = new Map<string, { integrationId: string; provider: ProviderType; boardUrl: string }>();
    for (const integration of this.integrations()) {
      if (!integration.active) continue;
      if (integration.providerType === 'GITHUB') continue;
      const host = this.hostOf(integration.boardUrl);
      if (!host) continue;
      if (!byHost.has(host)) {
        byHost.set(host, {
          integrationId: integration.id,
          provider: integration.providerType,
          boardUrl: integration.boardUrl,
        });
      }
    }

    const types = this.requirementTypes();
    return Array.from(byHost.entries()).map(([host, meta]) => {
      const synced = types
        .filter((t) => t.sourceHost === host && t.sourceProvider === meta.provider)
        .map((t) => t.value);
      return {
        host,
        provider: meta.provider,
        integrationId: meta.integrationId,
        boardUrl: meta.boardUrl,
        color: providerColor(meta.provider),
        providerLabel: providerLabel(meta.provider),
        syncedTypes: synced,
        isSynced: synced.length > 0,
      };
    });
  });

  readonly hasHosts = computed(() => this.hosts().length > 0);

  isBusy(host: string): boolean {
    return this.busyHosts().has(host);
  }

  syncHost(row: HostRow): void {
    if (this.isBusy(row.host)) return;
    this.markBusy(row.host);
    this.service.syncTypes(this.projectId(), row.integrationId).subscribe({
      next: () => {
        this.clearBusy(row.host);
        this.typesChanged.emit();
      },
      error: (err: unknown) => {
        this.clearBusy(row.host);
        this.notifyError(row, err);
      },
    });
  }

  removeHost(row: HostRow): void {
    if (this.isBusy(row.host)) return;
    this.markBusy(row.host);
    this.service.deleteByHost(this.projectId(), row.provider, row.host).subscribe({
      next: (response) => {
        this.clearBusy(row.host);
        this.notifyRemoved(response.kept);
        this.typesChanged.emit();
      },
      error: (err: unknown) => {
        this.clearBusy(row.host);
        this.notifyError(row, err);
      },
    });
  }

  private markBusy(host: string): void {
    this.busyHosts.update((set) => new Set(set).add(host));
  }

  private clearBusy(host: string): void {
    this.busyHosts.update((set) => {
      const next = new Set(set);
      next.delete(host);
      return next;
    });
  }

  private notifyRemoved(kept: string[]): void {
    const key = kept.length === 0 ? 'requirements.typeSync.removedAll' : 'requirements.typeSync.removedWithKept';
    const message = this.translate.instant(key, { count: kept.length, kept: kept.join(', ') });
    this.snackBar.open(message, undefined, { duration: 5000 });
  }

  /**
   * NEX-1077 Review: sichtbares Feedback bei Fehlern. Das BE liefert sprechende
   * Codes (412 JIRA_TOKEN_MISSING / JIRA_UNREACHABLE, 501 GitHub), die haeufigste
   * Ursache — fehlendes Token — bleibt sonst stumm. Absagen des Trackers und eine
   * deaktivierte Verbindung (409) meldet der gemeinsame Mapper, ein abgelehntes Token
   * mit dem Weg ins Konto (NEX-1354).
   */
  private notifyError(row: HostRow, err: unknown): void {
    const tracker = mapTrackerError(err, row.boardUrl);
    if (tracker) {
      this.notifyTrackerError(tracker);
      return;
    }
    const key = this.errorKey(err);
    const message = this.translate.instant(key, { host: row.host, provider: row.providerLabel });
    this.snackBar.open(message, undefined, { duration: 6000 });
  }

  private notifyTrackerError(tracker: TrackerError): void {
    const ref = this.snackBar.open(
      this.translate.instant(tracker.key, tracker.params),
      tracker.accountLink ? this.translate.instant(TRACKER_ERROR_KEYS.toAccount) : undefined,
      { duration: 6000 },
    );
    if (tracker.accountLink) {
      ref
        .onAction()
        .pipe(take(1))
        .subscribe(() => this.router.navigate(['/account']));
    }
  }

  private errorKey(err: unknown): string {
    if (!(err instanceof HttpErrorResponse)) return 'requirements.typeSync.errorGeneric';
    const body = (err.error ?? {}) as { code?: string };
    const code = typeof body.code === 'string' ? body.code : null;
    if (code === 'JIRA_TOKEN_MISSING' || code === 'REDMINE_TOKEN_MISSING') {
      return 'requirements.typeSync.errorTokenMissing';
    }
    if (code === 'JIRA_UNREACHABLE' || code === 'REDMINE_UNREACHABLE') {
      return 'requirements.typeSync.errorUnreachable';
    }
    if (err.status === 400) return 'requirements.typeSync.errorInactive';
    if (err.status === 501) return 'requirements.typeSync.errorUnsupported';
    if (err.status === 412) return 'requirements.typeSync.errorPrecondition';
    return 'requirements.typeSync.errorGeneric';
  }

  private hostOf(url: string): string {
    if (!url) return '';
    // NEX-1077 Review: `hostname` liefert den Host ohne Port. `URL.host` haette
    // fuer selbst gehostete Instanzen (`redmine.kunde.de:8443`) einen Port
    // dazugeklebt — der Vergleich mit dem BE-`source_host` (URI.getHost()) waere
    // dann nie erfolgreich.
    try {
      return new URL(url).hostname.toLowerCase();
    } catch {
      return url
        .replace(/^https?:\/\//i, '')
        .split('/')[0]
        .split(':')[0]
        .toLowerCase();
    }
  }
}
